import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableContainer from "../../../../components/Tables/TableContainer";
import TableCell from "../../../BillingAndPayments/components/TableCell";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  OWNER_AD_SPEND_COLUMN_COUNTRY,
  OWNER_AD_SPEND_COLUMN_PLATFORMS,
  OWNER_AD_SPEND_COLUMN_SHARE,
  OWNER_AD_SPEND_COLUMN_SPEND,
  OWNER_AD_SPEND_TOP_COUNTRIES_ARIA,
  OWNER_AD_SPEND_TOP_COUNTRIES_TITLE,
  OWNER_AD_SPEND_UNASSIGNED_COUNTRY,
} from "../../../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../../../utils/constants";
import AdSpendPlatformIcons from "./AdSpendPlatformIcons";
import {
  AD_SPEND_CARD_SX,
  formatAdSpendCountry,
  formatAdSpendPercent,
  formatAdSpendUsd,
} from "./adSpendDisplay";

const headerCellProps = { showTooltip: false, isHeader: true };
const bodyCellProps = {
  showTooltip: false,
  isHeader: false,
  showLastRowBorder: true,
};

const EMPTY_TOP_COUNTRIES = { rows: [], unassignedSpendUSD: 0 };

const AdSpendTopCountriesTable = ({
  topCountries = EMPTY_TOP_COUNTRIES,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslationApp();
  const language = i18n.language;
  const rows = topCountries.rows ?? EMPTY_TOP_COUNTRIES.rows;
  const unassignedSpendUSD = topCountries.unassignedSpendUSD || 0;
  const showUnassigned = unassignedSpendUSD > 0;

  const tableKeys = useMemo(() => {
    const keys = rows.map((row) => row.country);
    if (showUnassigned) keys.push("unassigned");
    return keys;
  }, [rows, showUnassigned]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t(OWNER_AD_SPEND_TOP_COUNTRIES_TITLE)}
      </Typography>
      <Box sx={{ ...AD_SPEND_CARD_SX, borderRadius: BORDER_RADIUS }}>
        <TableContainer
          loading={isLoading}
          keys={tableKeys}
          hasPagination={false}
          ariaLabel={t(OWNER_AD_SPEND_TOP_COUNTRIES_ARIA)}
          minTableWidth={640}
          sx={{ borderRadius: `${BORDER_RADIUS * 4}px !important` }}
        >
          <TableHead>
            <TableRow>
              <TableCell {...headerCellProps} sx={{ pl: "16px !important" }}>
                {t(OWNER_AD_SPEND_COLUMN_COUNTRY)}
              </TableCell>
              <TableCell {...headerCellProps}>
                {t(OWNER_AD_SPEND_COLUMN_SPEND)}
              </TableCell>
              <TableCell {...headerCellProps}>
                {t(OWNER_AD_SPEND_COLUMN_SHARE)}
              </TableCell>
              <TableCell {...headerCellProps}>
                {t(OWNER_AD_SPEND_COLUMN_PLATFORMS)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isLastRow = index === rows.length - 1 && !showUnassigned;
              return (
                <TableRow key={row.country}>
                  <TableCell
                    {...bodyCellProps}
                    isLastRow={isLastRow}
                    sx={{ pl: "16px !important" }}
                  >
                    {formatAdSpendCountry({ countryCode: row.country, t })}
                  </TableCell>
                  <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                    {formatAdSpendUsd({ value: row.spendUSD, language })}
                  </TableCell>
                  <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                    {formatAdSpendPercent({
                      percent: row.sharePercent,
                      language,
                    })}
                  </TableCell>
                  <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                    <AdSpendPlatformIcons platforms={row.platforms} />
                  </TableCell>
                </TableRow>
              );
            })}
            {showUnassigned ? (
              <TableRow>
                <TableCell
                  {...bodyCellProps}
                  isLastRow
                  sx={{ pl: "16px !important" }}
                >
                  {t(OWNER_AD_SPEND_UNASSIGNED_COUNTRY)}
                </TableCell>
                <TableCell {...bodyCellProps} isLastRow>
                  {formatAdSpendUsd({ value: unassignedSpendUSD, language })}
                </TableCell>
                <TableCell {...bodyCellProps} isLastRow>
                  {formatAdSpendPercent({
                    percent: topCountries.unassignedSharePercent,
                    language,
                  })}
                </TableCell>
                <TableCell {...bodyCellProps} isLastRow>
                  —
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default AdSpendTopCountriesTable;
